import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { updateExistingProtocol, fetchProtocolById, clearCurrentProtocol } from '../../../../protocolSlice';
import ProtocolBlockBuilder from './ProtocolBlockBuilder';
import './CreateProtocolForm.scss';

const EditProtocolForm: React.FC = () => {
  const dispatch = useAppDispatch() as any;
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { updating, error, currentUserProtocol, loading } = useAppSelector((state) => state.protocols);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    specialty: '',
    instructions: '',
  });

  const [jsonProtocol, setJsonProtocol] = useState('');

  // Load protocol data on component mount
  useEffect(() => {
    if (id) {
      // Очищаем текущий протокол перед загрузкой нового
      dispatch(clearCurrentProtocol());
      dispatch(fetchProtocolById(parseInt(id)));
    }

    // Очистка при размонтировании компонента
    return () => {
      dispatch(clearCurrentProtocol());
    };
  }, [dispatch, id]);

  // Fill form when protocol data is loaded
  const [isFormInitialized, setIsFormInitialized] = useState(false);

  useEffect(() => {
    if (currentUserProtocol && !isFormInitialized) {
      try {
        const parsedJson = JSON.parse(currentUserProtocol.content); // Заменили json_protocol на content

        const newFormData = {
          name: currentUserProtocol.name,
          description: currentUserProtocol.description || '',
          specialty: parsedJson.meta?.specialty || '',
          instructions: parsedJson['x-instructions'] || '',
        };

        setFormData(newFormData);
        setJsonProtocol(currentUserProtocol.content); // Заменили json_protocol на content
        setIsFormInitialized(true);
      } catch (error) {
        console.error('Failed to parse protocol JSON:', error);
      }
    }
  }, [currentUserProtocol, isFormInitialized]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleJsonChange = (value: string) => {
    setJsonProtocol(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !jsonProtocol.trim() || !id) {
      return;
    }

    try {
      const protocolData = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        content: jsonProtocol, // Заменили json_protocol на content
      };

      await dispatch(updateExistingProtocol({ id: parseInt(id), protocol: protocolData }));
      navigate('/protocols');
    } catch (error) {
      console.error('Failed to update protocol:', error);
    }
  };

  const isFormValid = formData.name.trim() && jsonProtocol.trim();

  if (loading && !currentUserProtocol) {
    return (
      <div className="page-content">
        <div className="d-flex justify-content-center align-items-center h-100">
          <div className="loading-container">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Загрузка...</span>
            </div>
            <p>Загрузка шаблона протокола...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!currentUserProtocol && !loading) {
    return (
      <div className="page-content">
        <div className="d-flex justify-content-center align-items-center h-100">
          <div className="error-container">
            <h3>Шаблон не найден</h3>
            <p>Шаблон с указанным ID не существует или недоступен</p>
            <button
              className="button-primary"
              onClick={() => navigate('/protocols')}
            >
              Вернуться к списку
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <form onSubmit={handleSubmit} className="protocol-form">
        <div className="create-protocol-header">
          <div className="header-left">
            <h1>Редактирование шаблона протокола</h1>
          </div>
          <div className="header-actions">
            <button
              type="button"
              className="button-secondary"
              onClick={() => navigate('/protocols')}
              disabled={updating}
            >
              Отмена
            </button>
            <button
              type="submit"
              className="button-primary"
              disabled={!isFormValid || updating}
            >
              {updating ? 'Сохранение...' : 'Сохранить изменения'}
            </button>
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="form-layout">
          {/* Левая колонка - информация о протоколе */}
          <div className="form-info-section">
            <div className="form-section mb-3">
              <h2>Основная информация</h2>

              <div className="form-group">
                <label htmlFor="name" className="form-label">
                  Название шаблона *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="search-input"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Введите название протокола"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="description" className="form-label">
                  Описание
                </label>
                <textarea
                  id="description"
                  name="description"
                  className="search-input textarea"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Краткое описание протокола"
                  rows={4}
                />
              </div>

              <div className="form-group">
                <label htmlFor="specialty" className="form-label">
                  Специальность
                </label>
                <input
                  type="text"
                  id="specialty"
                  name="specialty"
                  className="search-input"
                  value={formData.specialty}
                  onChange={handleInputChange}
                  placeholder="Укажите медицинскую специальность (например: терапия, кардиология)"
                />
              </div>

              <div className="form-group">
                <label htmlFor="instructions" className="form-label">
                  Общие инструкции по шаблону
                </label>
                <textarea
                  id="instructions"
                  name="instructions"
                  className="search-input textarea"
                  value={formData.instructions}
                  onChange={handleInputChange}
                  placeholder="Введите общие инструкции для заполнения шаблона"
                  rows={6}
                />
              </div>
            </div>

            <div className="form-section">
              <h2>Подсказки</h2>
              <div className="tips-container">
                <div className="tip-item">
                  <span className="tip-text">Используйте блоки для группировки связанных полей</span>
                </div>
                <div className="tip-item">
                  <span className="tip-text">Настройте типы полей в зависимости от данных</span>
                </div>
                <div className="tip-item">
                  <span className="tip-text">Отмечайте обязательные поля для валидации</span>
                </div>
              </div>
            </div>
          </div>

          {/* Правая колонка - конструктор */}
          <div className="form-builder-section">
            <ProtocolBlockBuilder
              onBlocksChange={() => { }}
              onJsonChange={handleJsonChange}
              specialty={formData.specialty}
              instructions={formData.instructions}
              existingJsonProtocol={currentUserProtocol?.content} // Заменили json_protocol на content
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditProtocolForm;
